import { Component, Inject, Input, OnDestroy, OnInit } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import get from 'lodash-es/get';
import isEmpty from 'lodash-es/isEmpty';

import { AccessibilityModalComponent } from '@finder/shared/components/a11y-modal/a11y-modal.component';
import { ModalComponent } from '@finder/shared/components/modal/modal.component';
import { ModalService } from '@finder/shared/components/modal/modal.service';
import { LocaleService } from '@finder/shared/services/locale-service/locale-service.service';
import { LocaleHelperService } from '@finder/shared/services/locale-service/locale-helper.service';
import { FinderApiConnectorService } from '@finder/shared/services/finder-api-connector/finder-api-connector.service';
import { GetFullDateStringProvider, GET_FULL_DATE_STRING } from '@finder/shared/utils/string/getFullDateString';
import { GetYMDProvider, GET_YMD } from '@finder/shared/utils/string/getYMD';
import getLocalizedTime from '@finder/shared/utils/string/getLocalizedTime';
import { GetAdjustedDateProvider, GET_ADJUSTED_DATE } from '@finder/shared/utils/date/getAdjustedDate';
import { CommonModule } from '@angular/common';
import { AnchorComponent } from '@finder/shared/components/anchor/anchor.component';
import { FontIconComponent } from '@finder/shared/components/font-icon/font-icon.component';
import { ButtonComponent } from '@finder/shared/components/button/button.component';
import { DatePickerWrapperComponent } from '@finder/shared/components/date-picker-wrapper/date-picker-wrapper.component';
import { LoadingSpinnerComponent } from '@finder/shared/components/loading-spinner/loading-spinner.component';

@Component({
    selector: 'finder-overview-aag',
    templateUrl: './overview-aag.component.html',
    styleUrls: ['./overview-aag.component.scss'],
    standalone: true,
    imports: [
        CommonModule,
        AnchorComponent,
        FontIconComponent,
        ButtonComponent,
        DatePickerWrapperComponent,
        LoadingSpinnerComponent
    ]
})
export class OverviewAagComponent implements OnInit, OnDestroy {
    @Input() data;

    a11yModal: ModalComponent;
    hasModal = false;
    showLinks = false;
    title: string;
    date: Date;
    info: string;
    formattedDate: string;
    layout: string;
    modalTitle: string;
    eventData: any = {};
    guestServicesLink: any;
    pdfLink: any;

    private destroy$ = new Subject<void>();

    constructor(
        private modalService: ModalService,
        private finderApi: FinderApiConnectorService,
        private localeService: LocaleService,
        private localeHelper: LocaleHelperService,
        @Inject(GET_ADJUSTED_DATE) private getAdjustedDate: GetAdjustedDateProvider,
        @Inject(GET_FULL_DATE_STRING) private getFullDateString: GetFullDateStringProvider,
        @Inject(GET_YMD) private getYMD: GetYMDProvider,
    ) {}

    ngOnInit() {
        /* eslint-disable max-len */
        /**
         * BREAKDOWN OF PEP LOGIC BELOW
         *
         * Finder Details Model:
         * https://github.disney.com/wdprd-development/mdx-php-library/blob/develop/src/application/modules/finder/models/FinderDetails.php
         *
         * LOCATION:
         * If `locationParkResort`
         *  If `ancestorParkLink.href`
         *      - get analyticsLinkId from `ancestorParkLink`
         *      - Create location with link
         *      - It looks like the link is just placed before the Location name?
         *        I can only guess that this must never have been used as it looks like the code is wrong
         *  Else show location name (`locationParkResort`)
         * Else If location exists show location name (`locationParkResort`)
         *
         * If `locationLandArea` show location name
         *
         * =====
         *
         * INFO:
         * Check for special event by looking at facets to see if one has the value of 'Special Events'
         * PEP overview pages check this and write out the details accordingly or handle everything else.
         *
         * The `detailsAtAGlance` function does the following:
         *      if a sort order array was not passed in it uses a default:
         *      'basicLocationInfo',
         *      'diningInfo',
         *      'diningPriceInfo',
         *      'magicPassesInfo',
         *      'moreDetailsInfo',
         *      'ratingInfo',
         *      'overviewScheduleDate',
         *      'overviewScheduleLocation'
         *
         * checks `specialEventScheduleInfo` and if so sets a param for `scheduleEvent = true`
         *
         * checks `overviewScheduleCalendar` and adds "summary" data (`summarizeScheduleData`)
         *      the helper checks if refurb status is true and doesn't proceed
         *      checks extraMagicHors, magicMorning, extraMagicHourMagicMorning, specialTicketedEventHours
         *          adds the title, startTime and endTime of each to an array
         *
         * Schedule lookup:
         * https://disneyworld.disney.go.com/finder/schedules/type/events/id/90004980%3BentityType%3DEvent/?isOverviewTemplate=1
         *
         * =====
         *
         * A11Y MODAL:
         * Multiple sections are created here:
         * https://github.disney.com/wdprd-development/mdx-php-library/blob/develop/src/application/modules/finder/views/scripts/detail/at-a-glance-items/moreDetailsInfo.phtml
         *      - Accessibility section (`accessibility`)
         *      - "Advisories", "Important Information About...", etc. (`aboutThis`)
         *      - Untitled paragraph about service animals (`serviceAnimals`)
         *      - Interests section (`interests`)
         *      - Rider Switch section (`additionalServices`)
         *      - Guest Policies section (`guestPolicies`)
         *      - Guest Services section (`guestServices`)
         *      - There is a TODO:
         *          //TODO: editorial copy goes here - but where does it come from?
         *          _ASSUME_ this means a way to show whatever a producer wants?
         *
         * Each of the keys ferom above are tested against content from _somewhere_?
         * Have not tracked that down yet...
         *
         * Facets/Keys/i18n for each section:
         * ------------------------
         * A11y: `mobilityDisabilities`, `hearingandVisualDisability`
         * About This: `aboutThis`
         * Svc Animals: `serviceAnimals`, `finder.details.modalIgnoredService.friendlyIds` (i18n)
         * Interests: `interests`
         * Rider Swap: `riderSwapAvailable` (key)
         * Policies: `guestPolicies` (this uses convoluted logic to determine)
         * Guest Svcs: `guestServices`
         */
        /* eslint-enable max-len */

        // IMPORTANT: These need to execute in a specific order
        this.getLocationLinks();
        this.getSchedule(this.data);
        this.getEvent();
        this.getModal();
    }

    ngOnDestroy() {
        // Ignore the if just for coverage, there's no need to test unsubscribe
        /* istanbul ignore next */
        this.destroy$.next();
        this.destroy$.complete();
    }

    /* istanbul ignore next */
    openModal() {
        this.a11yModal.open();
    }

    onDateChanged(e) {
        // Change to the selected date in the picker
        this.date = e;
        this.formattedDate = this.getFullDateString(this.date);

        // Get date in YYY-MM-DD format
        const selectedDate = this.getYMD(e);

        // Reset the schedule info
        this.info = '';

        this.finderApi.getSchedules(this.data.id, selectedDate)
            .pipe(takeUntil(this.destroy$))
            .subscribe(result => {
                // We need to structure the result in the same way so it can be processed properly
                // by the method
                this.getSchedule({ info: result }, true);
            });
    }

    private getLocationLinks() {
        const gsLink = get(this.data, 'guestServiceLink');
        const pdfLink = get(this.data, 'pdfLink');
        this.showLinks = !isEmpty(gsLink) || !isEmpty(pdfLink);

        if (!this.showLinks) {
            return;
        }

        if (gsLink) {
            this.guestServicesLink = {
                text: get(this.data, 'guestServiceLinkText'),
                url: gsLink,
            };
        }

        if (!isEmpty(pdfLink)) {
            this.pdfLink = pdfLink;
        }
    }

    private getSchedule(data, fromEvent = false) {
        const schedule = get(this.data, 'info.schedule');
        if (isEmpty(schedule)) {
            return;
        }

        // If this is called from the date changed event then we don't need to set these values
        // they are already set
        if (fromEvent === false) {
            this.layout = 'calendar';
            this.title = get(this.data, 'info.schedule.title');

            // If we have a schedule block but no schedules object, try getting 'today'
            // It may not be 100%, but it looks like special events may not return an array of
            // schedules even though they have valid schedules
            const schedulesObject = get(this.data, 'info.schedule.schedules');
            if (!schedulesObject) {
                this.onDateChanged(new Date());

                return;
            }
        }

        const schedules = get(data, 'info.schedule.schedules.Operating', {});
        if (isEmpty(schedules)) {
            this.info = get(this.data, 'info.schedule.noTimes');

            return;
        }

        const scheduleDate = schedules[Object.keys(schedules)[0]][0];
        this.getScheduleDetail(scheduleDate);
    }

    private getScheduleDetail(scheduleDate) {
        this.date = this.getAdjustedDate(scheduleDate.date);
        this.formattedDate = this.getFullDateString(scheduleDate.date);
        this.info = this.getScheduleInfo(scheduleDate);
    }

    private getScheduleInfo(scheduleDate) {
        const scheduleType = get(scheduleDate, 'group', '').toLowerCase();
        if (scheduleType === 'closed') {
            return get(this.data, 'info.schedule.noTimes');
        }

        const locale = this.localeHelper.getNormalizedLocale(this.localeService.getLocale());
        const startTime = getLocalizedTime(scheduleDate.startTime, locale);
        const endTime = getLocalizedTime(scheduleDate.endTime, locale);

        return `${startTime} to ${endTime}`;
    }

    private getEvent() {
        // Do not proceed if we already have a layout
        if (this.layout) {
            return;
        }

        this.title = get(this.data, 'info.titles.specialEvent');

        const startDate = get(this.data, 'info.startDate');
        const endDate = get(this.data, 'info.endDate');
        const [ startDateFormatted, startYear ] = get(this.data, 'info.startDateFormatted', '').split(', ');
        const [ endDateFormatted, endYear ] = get(this.data, 'info.endDateFormatted', '').split(', ');
        if (!endDate) {
            return;
        }

        this.layout = 'special';
        this.eventData = {
            startDate,
            startDateFormatted: startYear === endYear ? startDateFormatted : `${startDateFormatted}, ${startYear}`,
            endDate,
            endDateFormatted,
            endYear,
        };
    }

    private getModal() {
        // Do not proceed if we already have a layout
        if (this.layout) {
            return;
        }

        const aagInfo = get(this.data, 'info.data', []);
        if (!aagInfo.length) {
            return;
        }

        this.hasModal = true;
        this.modalTitle = get(this.data, 'info.titles.modal');
        this.a11yModal = this.modalService.create(
            AccessibilityModalComponent,
            {
                title: get(this.data, 'info.titles.modal'),
                hideOnClose: true,
                data: aagInfo,
                id: 'a11y-modal'
            }
        );
    }
}
